<template>
  <v-container fluid class="pa-4 pa-md-6">
    <div class="mb-4">
      <div class="text-h5">{{ $t("global_settings.title") }}</div>
    </div>

    <v-expansion-panels v-model="openPanels" multiple>
      <v-expansion-panel>
        <v-expansion-panel-title>
          {{ $t("global_settings.maintenance") }}
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-alert
            v-if="maintenance.error"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ maintenance.error }}
          </v-alert>
          <v-alert
            v-if="maintenance.saved"
            type="success"
            variant="tonal"
            class="mb-4"
          >
            {{ $t("maintenance_notice.saved") }}
          </v-alert>

          <v-card class="pa-4 mb-4" :loading="maintenance.loading" variant="outlined">
            <v-switch
              v-model="maintenance.form.enabled"
              color="success"
              hide-details
              :label="
                maintenance.form.enabled
                  ? $t('maintenance_notice.enabled')
                  : $t('maintenance_notice.disabled')
              "
            />
            <v-row class="mt-2">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="maintenance.form.date"
                  type="date"
                  :label="$t('maintenance_notice.date')"
                  variant="outlined"
                  density="comfortable"
                  clearable
                />
              </v-col>
              <v-col cols="6" md="4">
                <v-text-field
                  v-model="maintenance.form.startTime"
                  type="time"
                  :label="$t('maintenance_notice.start_time')"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="6" md="4">
                <v-text-field
                  v-model="maintenance.form.endTime"
                  type="time"
                  :label="$t('maintenance_notice.end_time')"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
            </v-row>
            <div class="text-caption text-medium-emphasis mb-4">
              {{ $t("maintenance_notice.timezone_hint") }}
            </div>
            <div class="d-flex justify-end">
              <v-btn
                color="red-darken-4"
                :loading="maintenance.saving"
                @click="saveMaintenance"
              >
                {{ $t("maintenance_notice.save") }}
              </v-btn>
            </div>
          </v-card>

          <v-card class="pa-4" variant="outlined">
            <div class="text-subtitle-1 mb-1">
              {{ $t("maintenance_notice.preview") }}
            </div>
            <div class="text-caption text-medium-emphasis mb-4">
              {{ $t("maintenance_notice.edit_hint") }}
            </div>
            <div v-for="lang in languages" :key="lang.locale" class="mb-3">
              <div class="text-caption text-medium-emphasis">{{ lang.title }}</div>
              <v-alert
                type="warning"
                variant="tonal"
                icon="mdi-alert-circle-outline"
              >
                <div
                  class="maintenance-message-edit"
                  contenteditable="true"
                  spellcheck="true"
                  :data-locale="lang.locale"
                  @input="onMessageInput(lang.locale, $event)"
                  @blur="onMessageBlur(lang.locale, $event)"
                  @keydown="onMessageKeydown"
                />
                <div class="d-flex justify-end mt-3">
                  <v-btn
                    color="red-darken-4"
                    size="small"
                    :loading="maintenance.saving"
                    @click="saveMaintenance"
                  >
                    {{ $t("maintenance_notice.save") }}
                  </v-btn>
                </div>
              </v-alert>
            </div>
          </v-card>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          {{ $t("global_settings.refused_catalog_title") }}
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-alert
            v-if="catalog.error"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ catalog.error }}
          </v-alert>
          <v-alert
            v-if="catalog.saved"
            type="success"
            variant="tonal"
            class="mb-4"
          >
            {{ $t("global_settings.saved") }}
          </v-alert>

          <v-card class="pa-4" :loading="catalog.loading" variant="outlined">
            <div class="text-body-2 text-medium-emphasis mb-3">
              {{ $t("global_settings.refused_catalog_title_hint") }}
            </div>
            <v-switch
              v-model="catalog.enabled"
              color="success"
              hide-details
              :label="
                catalog.enabled
                  ? $t('global_settings.master_enabled')
                  : $t('global_settings.master_disabled')
              "
            />
            <v-alert
              v-if="!catalog.enabled"
              type="warning"
              variant="tonal"
              class="mt-3 mb-3"
            >
              {{ $t("global_settings.master_off_warning") }}
            </v-alert>
            <div class="text-caption text-medium-emphasis mt-2 mb-4">
              {{ $t("global_settings.ebay_note") }}
            </div>
            <v-text-field
              v-model="catalog.search"
              :label="$t('global_settings.search_institutions')"
              variant="outlined"
              density="comfortable"
              clearable
              hide-details
              class="mb-3"
              prepend-inner-icon="mdi-magnify"
            />
            <div class="text-subtitle-2 mb-2">
              {{ $t("global_settings.institutions") }}
              ({{ catalog.institutionIds.length }})
            </div>
            <div class="global-settings-institution-list">
              <div
                v-for="institution in filteredInstitutions"
                :key="institution._id"
                class="global-settings-institution-row"
              >
                <span class="text-body-2">{{ institution.title }}</span>
                <v-switch
                  :model-value="catalog.institutionIds.includes(institution._id)"
                  color="success"
                  hide-details
                  density="compact"
                  inset
                  class="ma-0 pa-0"
                  @update:model-value="toggleInstitution(institution._id, $event)"
                />
              </div>
            </div>
            <div class="d-flex justify-end mt-4">
              <v-btn
                color="red-darken-4"
                :loading="catalog.saving"
                @click="saveCatalogTitle"
              >
                {{ $t("global_settings.save") }}
              </v-btn>
            </div>
          </v-card>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          {{ $t("global_settings.compliance") }}
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-alert
            v-if="compliance.error"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ compliance.error }}
          </v-alert>
          <v-alert
            v-if="compliance.saved"
            type="success"
            variant="tonal"
            class="mb-4"
          >
            {{ $t("global_settings.saved") }}
          </v-alert>

          <v-card class="pa-4" :loading="catalog.loading" variant="outlined">
            <div class="text-body-2 text-medium-emphasis mb-3">
              {{ $t("global_settings.compliance_hint") }}
            </div>
            <v-switch
              v-model="compliance.enabled"
              color="success"
              hide-details
              :label="
                compliance.enabled
                  ? $t('global_settings.compliance_enabled')
                  : $t('global_settings.compliance_disabled')
              "
            />
            <div class="d-flex justify-end mt-4">
              <v-btn
                color="red-darken-4"
                :loading="compliance.saving"
                @click="saveCompliance"
              >
                {{ $t("global_settings.save") }}
              </v-btn>
            </div>
          </v-card>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-container>
</template>

<script>
import getRequest from "@/api/getRequest";
import putRequest from "@/api/putRequest";
import {
  escapeHtml,
  formatMaintenanceDate,
  PLACEHOLDERS,
} from "@/components/MaintenanceBanner.vue";

const LANGUAGES = [
  { locale: "it", title: "Italiano" },
  { locale: "en", title: "English" },
  { locale: "sq", title: "Shqip" },
  { locale: "de", title: "Deutsch" },
  { locale: "fr", title: "Français" },
  { locale: "es", title: "Español" },
];

function emptyMessages() {
  return LANGUAGES.reduce((acc, lang) => ({ ...acc, [lang.locale]: "" }), {});
}

export default {
  name: "GlobalSettings",
  data() {
    return {
      languages: LANGUAGES,
      openPanels: [0],
      maintenance: {
        loading: false,
        saving: false,
        saved: false,
        error: "",
        seed: 0,
        form: {
          enabled: false,
          date: "",
          startTime: "07:00",
          endTime: "17:00",
          customMessages: emptyMessages(),
        },
      },
      catalog: {
        loading: false,
        saving: false,
        saved: false,
        error: "",
        enabled: false,
        institutionIds: [],
        institutions: [],
        search: "",
      },
      compliance: {
        saving: false,
        saved: false,
        error: "",
        enabled: false,
      },
    };
  },
  computed: {
    filteredInstitutions() {
      const q = (this.catalog.search || "").trim().toLowerCase();
      if (!q) return this.catalog.institutions;
      return this.catalog.institutions.filter((i) =>
        (i.title || "").toLowerCase().includes(q),
      );
    },
  },
  created() {
    this.loadMaintenance();
    this.loadCatalogTitle();
  },
  mounted() {
    this.fillMessageEditors();
  },
  watch: {
    openPanels() {
      if (this.openPanels.includes(0)) {
        this.$nextTick(() => this.fillMessageEditors(true));
      }
    },
    "maintenance.seed"() {
      this.$nextTick(() => this.fillMessageEditors(true));
    },
    "maintenance.form.date"() {
      this.refreshTokenLabels();
    },
    "maintenance.form.startTime"() {
      this.refreshTokenLabels();
    },
    "maintenance.form.endTime"() {
      this.refreshTokenLabels();
    },
  },
  methods: {
    applyMaintenance(data) {
      this.maintenance.form = {
        enabled: Boolean(data.enabled),
        date: data.date || "",
        startTime: data.startTime || "07:00",
        endTime: data.endTime || "17:00",
        customMessages: { ...emptyMessages(), ...(data.customMessages || {}) },
      };
      this.maintenance.seed += 1;
    },
    async loadMaintenance() {
      this.maintenance.loading = true;
      this.maintenance.error = "";
      try {
        this.applyMaintenance(await getRequest("/maintenance-notice/admin"));
      } catch (err) {
        this.maintenance.error = this.errorText(err);
      } finally {
        this.maintenance.loading = false;
      }
    },
    async saveMaintenance() {
      this.maintenance.saving = true;
      this.maintenance.saved = false;
      this.maintenance.error = "";
      this.syncMessageEditors();
      try {
        const res = await putRequest("/maintenance-notice/admin", "", {
          ...this.maintenance.form,
          date: this.maintenance.form.date || null,
        });
        this.applyMaintenance(res.data);
        this.maintenance.saved = true;
        setTimeout(() => {
          this.maintenance.saved = false;
        }, 3000);
      } catch (err) {
        this.maintenance.error = this.errorText(err);
      } finally {
        this.maintenance.saving = false;
      }
    },
    async loadCatalogTitle() {
      this.catalog.loading = true;
      this.catalog.error = "";
      try {
        const data = await getRequest("/global-settings/admin");
        this.catalog.enabled = Boolean(data.refusedCatalogTitle?.enabled);
        this.catalog.institutionIds = data.refusedCatalogTitle?.institutionIds || [];
        this.catalog.institutions = data.institutions || [];
        this.compliance.enabled = data.dismantlingCompliance?.enabled === true;
      } catch (err) {
        this.catalog.error = this.errorText(err);
        this.compliance.error = this.errorText(err);
      } finally {
        this.catalog.loading = false;
      }
    },
    toggleInstitution(id, checked) {
      if (checked) {
        if (!this.catalog.institutionIds.includes(id)) {
          this.catalog.institutionIds = [...this.catalog.institutionIds, id];
        }
        this.catalog.enabled = true;
      } else {
        this.catalog.institutionIds = this.catalog.institutionIds.filter(
          (item) => item !== id,
        );
      }
    },
    async saveCatalogTitle() {
      this.catalog.saving = true;
      this.catalog.saved = false;
      this.catalog.error = "";
      try {
        const res = await putRequest(
          "/global-settings/admin/refused-catalog-title",
          "",
          {
            enabled: this.catalog.enabled,
            institutionIds: this.catalog.institutionIds,
          },
        );
        this.catalog.enabled = Boolean(res.data.enabled);
        this.catalog.institutionIds = res.data.institutionIds || [];
        this.catalog.saved = true;
        setTimeout(() => {
          this.catalog.saved = false;
        }, 3000);
      } catch (err) {
        this.catalog.error = this.errorText(err);
      } finally {
        this.catalog.saving = false;
      }
    },
    async saveCompliance() {
      this.compliance.saving = true;
      this.compliance.saved = false;
      this.compliance.error = "";
      try {
        const res = await putRequest(
          "/global-settings/admin/dismantling-compliance",
          "",
          { enabled: this.compliance.enabled === true },
        );
        this.compliance.enabled = res.data.enabled === true;
        this.compliance.saved = true;
        setTimeout(() => {
          this.compliance.saved = false;
        }, 3000);
      } catch (err) {
        this.compliance.error = this.errorText(err);
      } finally {
        this.compliance.saving = false;
      }
    },
    errorText(err) {
      return (
        (err &&
          err.response &&
          err.response.data &&
          err.response.data.errorMsg) ||
        (err && err.message) ||
        "Error"
      );
    },
    defaultText(locale) {
      return this.$t("maintenance_notice.banner_text", PLACEHOLDERS, { locale });
    },
    messageTemplate(locale) {
      const custom = (this.maintenance.form.customMessages[locale] || "").trim();
      return custom || this.defaultText(locale);
    },
    tokenLabel(token, locale) {
      const form = this.maintenance.form;
      if (token === "start") return form.startTime || "{start}";
      if (token === "end") return form.endTime || "{end}";
      if (token === "date") return formatMaintenanceDate(form.date, locale) || "{date}";
      return "";
    },
    editorHtml(locale) {
      const tokenHtml = (name) =>
        `<b contenteditable="false" data-token="${name}">${escapeHtml(
          this.tokenLabel(name, locale),
        )}</b>`;
      return escapeHtml(this.messageTemplate(locale))
        .replaceAll("{date}", tokenHtml("date"))
        .replaceAll("{start}", tokenHtml("start"))
        .replaceAll("{end}", tokenHtml("end"));
    },
    messageEditors() {
      if (!this.$el || !this.$el.querySelectorAll) return [];
      return Array.from(this.$el.querySelectorAll(".maintenance-message-edit"));
    },
    fillMessageEditors(force) {
      const seed = String(this.maintenance.seed);
      this.messageEditors().forEach((el) => {
        const locale = el.dataset.locale;
        if (!locale) return;
        const empty = !(el.textContent || "").trim();
        if (!force && !empty && el.dataset.filled === seed) return;
        if (document.activeElement === el && !empty) return;
        el.innerHTML = this.editorHtml(locale);
        el.dataset.filled = seed;
      });
    },
    refreshTokenLabels() {
      this.messageEditors().forEach((el) => {
        const locale = el.dataset.locale;
        el.querySelectorAll("[data-token]").forEach((node) => {
          node.textContent = this.tokenLabel(node.dataset.token, locale);
        });
      });
    },
    readTemplate(root) {
      const seen = { date: false, start: false, end: false };
      let out = "";
      const walk = (node) => {
        node.childNodes.forEach((child) => {
          if (child.nodeType === Node.TEXT_NODE) {
            out += child.textContent;
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            const token = child.dataset && child.dataset.token;
            if (token && Object.prototype.hasOwnProperty.call(seen, token)) {
              if (!seen[token]) {
                out += `{${token}}`;
                seen[token] = true;
              }
            } else if (child.tagName === "BR") {
              out += " ";
            } else {
              walk(child);
            }
          }
        });
      };
      walk(root);
      return {
        text: out.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim(),
        complete: seen.date && seen.start && seen.end,
      };
    },
    applyEditorText(locale, el) {
      const parsed = this.readTemplate(el);
      if (!parsed.complete) {
        el.innerHTML = this.editorHtml(locale);
        return;
      }
      const fallback = this.defaultText(locale).replace(/\s+/g, " ").trim();
      this.maintenance.form.customMessages[locale] =
        parsed.text === fallback ? "" : parsed.text;
    },
    onMessageInput(locale, event) {
      this.applyEditorText(locale, event.currentTarget);
    },
    onMessageBlur(locale, event) {
      this.applyEditorText(locale, event.currentTarget);
      event.currentTarget.innerHTML = this.editorHtml(locale);
    },
    onMessageKeydown(event) {
      if (event.key === "Enter") event.preventDefault();
    },
    syncMessageEditors() {
      this.messageEditors().forEach((el) => {
        if (el.dataset.locale) this.applyEditorText(el.dataset.locale, el);
      });
    },
  },
};
</script>

<style scoped>
.global-settings-institution-list {
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 4px 12px;
}
.global-settings-institution-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 40px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.global-settings-institution-row:last-child {
  border-bottom: 0;
}
.maintenance-message-edit {
  outline: none;
  cursor: text;
  min-height: 1.5em;
  line-height: 1.5;
}
.maintenance-message-edit:focus {
  outline: 1px dashed rgba(0, 0, 0, 0.35);
  outline-offset: 4px;
  border-radius: 4px;
}
.maintenance-message-edit :deep(b) {
  font-weight: 700;
  cursor: default;
  user-select: none;
}
</style>
